/**
 * Mock API for local development without a backend.
 *
 * HOW TO ENABLE:
 *   Set "USE_MOCKS": true in public/config.json (it's true by default).
 *   The barrel export in api/index.ts will route all API calls here
 *   instead of hitting the real backend.
 *
 * HOW TO CUSTOMIZE MOCK DATA:
 *   - Edit SEED_DATA below to change the field values returned by the mock.
 *   - Typed against IntakeFormData, so the compiler catches typos.
 *   - Data lives in memory and resets on page refresh.
 *
 * HOW TO ADD MOCK DATA FOR A NEW FIELD (per-step stories):
 *   1. Add the field to IntakeFormData in types/form.ts
 *   2. Add a seed value here in SEED_DATA (optional — only if you want a default)
 *   3. Add it to EMPTY_FORM_DATA with a default value
 *
 * UNDERSCORE-PREFIXED PARAMETERS (e.g. _formId, _email):
 *   Parameters prefixed with _ are unused in the mock but required to match
 *   the real API interface (intakeApi.ts). This is standard TypeScript convention.
 */
import {
  EMPTY_FORM_DATA,
  uuid,
  type IntakeFormResponse,
  type LockResponse,
  type FileUploadResponse,
  type IntakeFormData,
} from "../types";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const randomDelay = () => delay(200 + Math.random() * 400);

// Sample data returned by the mock API
// Per AC, only the AC-mapped fields (Effective Date, Company Legal Name,
// Federal Tax ID, address block, SIC) prefill on V1 — DBA, Organization Type,
// Year Established, and the W2 counts must stay blank for the user to fill in.
const SEED_DATA: Partial<IntakeFormData> = {
  effectiveDate: "2026-07-01",
  companyLegalName: "Acme Widget Corp",
  federalTaxId: "12-3456789",
  streetAddress: "123 Main St",
  city: "New York",
  state: "NY",
  zipCode: "10001",
  sicCode: "3599",
  avgW2Employees: 45,
  contactName: "Jane Smith",
  contactTitle: "HR Director",
  contactEmail: "jane.smith@acmecorp.com",
  contactPhone: "(212) 555-0100",
  fiduciaryName: "Robert Jones",
  fiduciaryTitle: "CFO",
  fiduciaryEmail: "robert.jones@acmecorp.com",
  fiduciaryPhone: "(212) 555-0101",
  // Step 3: Company Structure
  owners: (() => {
    const johnId = uuid();
    const janeId = uuid();
    return [
      { id: johnId, firstName: "John", lastName: "Smith", mi: "A" },
      { id: janeId, firstName: "Jane", lastName: "Doe", mi: "" },
    ];
  })(),
  // Step 4 (Coverage) — drives Prior Carrier Bill and Required Documents visibility
  replacingMedical: "Yes" as "Yes" | "No" | null,
  // Step 1 — drives org-type document requirements
  orgType: "S Corporation",
  // Step 7 — drives Required Documents visibility
  totalEnrolledEmployees: 15,
};

// In-memory store
const forms = new Map<string, IntakeFormResponse>();

function getOrCreateForm(formId: string): IntakeFormResponse {
  const existing = forms.get(formId);
  if (existing) return existing;

  const form: IntakeFormResponse = {
    id: formId,
    proposalId: "PROP-2026-001",
    version: 1,
    status: "draft",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    data: { ...EMPTY_FORM_DATA, ...SEED_DATA },
    prefilled: {
      effectiveDate: true,
      companyLegalName: true,
      federalTaxId: true,
      streetAddress: true,
      city: true,
      state: true,
      zipCode: true,
      sicCode: true,
      avgW2Employees: true,
      contactName: true,
      contactTitle: true,
      contactEmail: true,
      contactPhone: true,
      fiduciaryName: true,
      fiduciaryTitle: true,
      fiduciaryEmail: true,
      fiduciaryPhone: true,
    },
    baseline: {
      effectiveDate: SEED_DATA.effectiveDate,
      companyLegalName: SEED_DATA.companyLegalName,
      federalTaxId: SEED_DATA.federalTaxId,
      streetAddress: SEED_DATA.streetAddress,
      city: SEED_DATA.city,
      state: SEED_DATA.state,
      zipCode: SEED_DATA.zipCode,
      sicCode: SEED_DATA.sicCode,
      avgW2Employees: SEED_DATA.avgW2Employees,
      contactName: SEED_DATA.contactName,
      contactTitle: SEED_DATA.contactTitle,
      contactEmail: SEED_DATA.contactEmail,
      contactPhone: SEED_DATA.contactPhone,
      fiduciaryName: SEED_DATA.fiduciaryName,
      fiduciaryTitle: SEED_DATA.fiduciaryTitle,
      fiduciaryEmail: SEED_DATA.fiduciaryEmail,
      fiduciaryPhone: SEED_DATA.fiduciaryPhone,
    },
    mismatches: {},
    staleFileIds: [],
    employeesModified: false,
    meta: {
      medicalCarrierOptions: [
        "ABA (American Benefit Administrators)", "Aetna", "Allied National",
        "AllSavers / United Health Care", "Allstate / Nationwide", "Angle",
        "ATA", "Blue Cross Blue Shield", "Cigna", "Eagle", "Humana", "Kaiser",
        "Medical Mutual", "Sana", "Starmark / Trustmark", "Other",
      ],
      // Product-confirmed picklist for Organization Type — matches the
      // restricted picklist on Employer_Intake_Form__c.Organization_Type__c.
      organizationTypeOptions: [
        "Sole Proprietorship (Farm & Non-Farm)",
        "Single Member LLC",
        "PLLC / PC / PA",
        "Partnerships (LP/LLP)",
        "Multi-Member LLC (Partnership Treatment)",
        "S Corporation",
        "Grantor Trusts",
      ],
      sicCodes: [],
      excludedClassesOptions: ["Union", "Hourly", "Part-Time", "Seasonal", "Other"],
      waitingPeriodOptions: ["Immediate", "30 days", "60 days", "90 days", "1st of month following 30 days"],
      uwMethod: null,
      isRenewal: false,
      availablePlans: 4,
      ihqLink: "https://ihq.optimyl.com/login?contractId=DEMO-12345",
    },
  };
  forms.set(formId, form);
  return form;
}

export async function getIntakeForm(formId: string): Promise<IntakeFormResponse> {
  await randomDelay();
  return structuredClone(getOrCreateForm(formId));
}

export async function getIntakeFormByProposal(
  proposalId: string,
): Promise<IntakeFormResponse | null> {
  await randomDelay();
  for (const form of forms.values()) {
    if (form.proposalId === proposalId) return structuredClone(form);
  }
  return null;
}

export async function createIntakeForm(
  proposalId: string,
): Promise<IntakeFormResponse> {
  await randomDelay();
  const form: IntakeFormResponse = {
    ...structuredClone(getOrCreateForm(`mock-${Date.now()}`)),
    proposalId,
  };
  forms.set(form.id, form);
  return structuredClone(form);
}

export async function saveDraft(
  formId: string,
  data: Partial<IntakeFormData>,
): Promise<IntakeFormResponse> {
  await randomDelay();
  const form = getOrCreateForm(formId);
  form.data = { ...form.data, ...data };
  form.updatedAt = new Date().toISOString();
  return structuredClone(form);
}

export async function submitIntakeForm(
  formId: string,
): Promise<IntakeFormResponse> {
  await randomDelay();
  const form = getOrCreateForm(formId);
  form.status = "submitted";
  form.updatedAt = new Date().toISOString();
  return structuredClone(form);
}

export async function uploadFile(
  _formId: string,
  _docType: string,
  file: File,
): Promise<FileUploadResponse> {
  await randomDelay();
  return {
    fileId: `file-${Date.now()}`,
    name: file.name,
    size: file.size,
  };
}

export async function deleteFile(_fileId: string): Promise<void> {
  await randomDelay();
}

export async function acquireLock(
  _formId: string,
  _email: string,
): Promise<LockResponse> {
  await randomDelay();
  return { acquired: true };
}

export async function releaseLock(_formId: string): Promise<void> {
  await randomDelay();
}
